import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { useMemo } from 'react';
import { ToastProvider } from '../components/ui';
import { ParticleBackground, LOGIN_CAMERA, ROUTE_CAMERAS } from '../components/background/ParticleBackground';

export const Route = createRootRoute({
  component: RootComponent,
});

// Get camera settings based on current route
function getCameraForRoute(pathname: string) {
  if (pathname === '/login') {
    return LOGIN_CAMERA;
  }

  // Match route to camera (handle sub-routes like /invoices/new)
  if (pathname === '/' || pathname.startsWith('/dashboard')) {
    return ROUTE_CAMERAS.dashboard;
  }
  if (pathname.startsWith('/customers')) {
    return ROUTE_CAMERAS.customers;
  }
  if (pathname.startsWith('/invoices')) {
    return ROUTE_CAMERAS.invoices;
  }
  if (pathname.startsWith('/contracts')) {
    return ROUTE_CAMERAS.contracts;
  }
  if (pathname.startsWith('/estimates')) {
    return ROUTE_CAMERAS.estimates;
  }
  if (pathname === '/settings/animation') {
    return ROUTE_CAMERAS.animation;
  }
  if (pathname.startsWith('/settings')) {
    return ROUTE_CAMERAS.settings;
  }

  // Default fallback
  return ROUTE_CAMERAS.dashboard;
}

function RootComponent() {
  const location = useLocation();

  // Load animation settings from localStorage (persists across routes)
  const animationSettings = useMemo(() => ({
    speed: parseFloat(localStorage.getItem('animation_speed') || '0.002'),
    particleCount: {
      x: parseInt(localStorage.getItem('animation_particle_x') || '80'),
      z: parseInt(localStorage.getItem('animation_particle_z') || '40'),
    },
    waveAmplitude: parseFloat(localStorage.getItem('animation_wave') || '2.5'),
    noiseIntensity: parseFloat(localStorage.getItem('animation_noise') || '1.2'),
  }), []);

  // Get camera settings for current route
  const cameraSettings = getCameraForRoute(location.pathname);

  return (
    <ToastProvider>
      {/* Particle Background - rendered at root level to persist across route changes */}
      <ParticleBackground {...animationSettings} camera={cameraSettings} />
      <Outlet />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </ToastProvider>
  );
}
